import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Car, TrendingUp, Building2, ChevronRight, ChevronLeft, Send, Check, DollarSign, Calendar, FileText, HelpCircle, User } from 'lucide-react';
// --- Types & Data ---
type FormData = {
  usage: string;
  vehicleTypes: string[];
  budget: string;
  minYear: string;
  titlePreference: string;
  hasImported: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  acceptedTerms: boolean;
};
const INITIAL_DATA: FormData = {
  usage: '',
  vehicleTypes: [],
  budget: '',
  minYear: '',
  titlePreference: '',
  hasImported: '',
  name: '',
  email: '',
  phone: '',
  message: '',
  acceptedTerms: false
};
const USAGE_OPTIONS = [{
  id: 'personal',
  label: 'Uso Personal',
  icon: User,
  desc: 'Busco mi próximo auto para uso diario o familiar.'
}, {
  id: 'resale',
  label: 'Reventa',
  icon: TrendingUp,
  desc: 'Quiero invertir para reparar y vender con margen.'
}, {
  id: 'business',
  label: 'Negocio / Flota',
  icon: Building2,
  desc: 'Vehículos de trabajo, transporte o Uber/InDrive.'
}];
const VEHICLE_TYPES = ['Sedán', 'SUV / Crossover', 'Pick-up', 'Coupé / Deportivo', 'Minivan', 'Hatchback', 'No tengo preferencia'];
const BUDGET_RANGES = [{
  id: '3.5-5k',
  label: '$3,500 – $5,000',
  desc: 'Ideal para primeros autos o proyectos.'
}, {
  id: '5-8k',
  label: '$5,000 – $8,000',
  desc: 'Gama media, años 2018-2020 aprox.'
}, {
  id: '8-11k',
  label: '$8,000 – $11,000',
  desc: 'Vehículos modernos, 2020+.'
}, {
  id: '11k+',
  label: '$11,000+',
  desc: 'Gama alta, camionetas o años recientes.'
}];
// 🇸🇻 Ley de 8 años: el año mínimo permitido se calcula dinámicamente
const CURRENT_YEAR = new Date().getFullYear();
const MIN_LEGAL_YEAR = CURRENT_YEAR - 8;
const YEAR_OPTIONS = [`${MIN_LEGAL_YEAR}+`, `${MIN_LEGAL_YEAR + 2}+`, `${MIN_LEGAL_YEAR + 4}+`, 'No importa'];
const TITLE_OPTIONS = ['Clean Title', 'Salvage', 'Rebuildable'];
// ============================================================
// ⚙️ CONFIGURACIÓN
const EMAILJS_SERVICE_ID = 'service_m7m0m09';
const EMAILJS_TEMPLATE_ID = 'template_hyqrklh';
const EMAILJS_PUBLIC_KEY = 'w8t9KyY5LzV6mitFs';
// ============================================================
export function CTASection() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0); // -1 for back, 1 for next
  const [formData, setFormData] = useState<FormData>(INITIAL_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const totalSteps = 5;
  // --- Handlers ---
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const toggleVehicleType = (type: string) => {
    setFormData(prev => {
      const current = prev.vehicleTypes;
      if (current.includes(type)) {
        return {
          ...prev,
          vehicleTypes: current.filter(t => t !== type)
        };
      } else {
        // If selecting "No preference", clear others. If selecting others, clear "No preference"
        if (type === 'No tengo preferencia') {
          return {
            ...prev,
            vehicleTypes: [type]
          };
        }
        const newTypes = current.filter(t => t !== 'No tengo preferencia');
        return {
          ...prev,
          vehicleTypes: [...newTypes, type]
        };
      }
    });
  };
  const nextStep = () => {
    if (step < totalSteps) {
      setDirection(1);
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Build readable labels
    const usageLabel = USAGE_OPTIONS.find(o => o.id === formData.usage)?.label || formData.usage;
    const budgetLabel = BUDGET_RANGES.find(b => b.id === formData.budget)?.label || formData.budget;
    // --- Send email automatically via EmailJS ---
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        usage: usageLabel,
        vehicle_types: formData.vehicleTypes.join(', '),
        budget: budgetLabel,
        min_year: formData.minYear,
        title_preference: formData.titlePreference,
        has_imported: formData.hasImported,
        message: formData.message || 'Sin mensaje adicional'
      }, EMAILJS_PUBLIC_KEY);
    } catch (error) {
      console.error('EmailJS error:', error);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  // --- Animation Variants ---
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 20 : -20,
      opacity: 0
    })
  };
  // --- Render Steps ---
  const renderStep = () => {
    switch (step) {
      case 1:
        // Usage
        return <div className="space-y-4" data-id="element-41">
            <h3 className="text-xl font-bold text-earia-text-heading mb-6" data-id="element-42">
              ¿Qué uso le darás al vehículo?
            </h3>
            <div className="grid grid-cols-1 gap-4" data-id="element-43">
              {USAGE_OPTIONS.map(option => <button type="button" key={option.id} onClick={() => updateField('usage', option.id)} className={`relative flex items-start p-4 rounded-xl border-2 transition-all text-left group ${formData.usage === option.id ? 'border-earia-accent bg-earia-accent/5 shadow-md' : 'border-earia-border bg-white hover:border-earia-accent/50'}`} data-id="element-44">
                  <div className={`p-3 rounded-lg mr-4 transition-colors ${formData.usage === option.id ? 'bg-earia-accent text-earia-text-heading' : 'bg-earia-bg-alt text-earia-text-body group-hover:text-earia-accent-hover'}`} data-id="element-45">
                    <option.icon className="w-6 h-6" data-id="element-46" />
                  </div>
                  <div className="flex-1" data-id="element-47">
                    <div className="flex justify-between items-center mb-1" data-id="element-48">
                      <span className="font-bold text-earia-text-heading" data-id="element-49">
                        {option.label}
                      </span>
                      {formData.usage === option.id && <Check className="w-5 h-5 text-earia-accent" data-id="element-50" />}
                    </div>
                    <p className="text-sm text-earia-text-body" data-id="element-51">
                      {option.desc}
                    </p>
                  </div>
                </button>)}
            </div>
          </div>;
      case 2:
        // Vehicle Type
        return <div className="space-y-6" data-id="element-52">
            <h3 className="text-xl font-bold text-earia-text-heading" data-id="element-53">
              ¿Qué tipo de vehículo te interesa?
            </h3>
            <p className="text-sm text-earia-text-body -mt-4 mb-4" data-id="element-54">
              Puedes seleccionar varias opciones.
            </p>
            <div className="flex flex-wrap gap-3" data-id="element-55">
              {VEHICLE_TYPES.map(type => {
              const isSelected = formData.vehicleTypes.includes(type);
              return <button type="button" key={type} onClick={() => toggleVehicleType(type)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${isSelected ? 'bg-earia-accent text-earia-text-heading border-earia-accent shadow-sm' : 'bg-white text-earia-text-body border-earia-border hover:border-earia-accent/50'}`} data-id="element-56">
                    {type}
                  </button>;
            })}
            </div>
            {formData.vehicleTypes.length === 0 && <p className="text-xs text-earia-danger mt-2 flex items-center gap-1" data-id="element-57">
                <HelpCircle className="w-3 h-3" data-id="element-58" /> Selecciona al menos una
                opción
              </p>}
          </div>;
      case 3:
        // Budget
        return <div className="space-y-6" data-id="element-59">
            <div data-id="element-60">
              <h3 className="text-xl font-bold text-earia-text-heading" data-id="element-61">
                ¿Cuál es tu presupuesto total?
              </h3>
              <p className="text-sm text-earia-text-body mt-1" data-id="element-62">
                Incluye subasta, impuestos, flete y reparación estimada.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-63">
              {BUDGET_RANGES.map(range => <button type="button" key={range.id} onClick={() => updateField('budget', range.id)} className={`p-4 rounded-xl border-2 transition-all text-left h-full flex flex-col justify-between ${formData.budget === range.id ? 'border-earia-accent bg-earia-accent/5 shadow-md' : 'border-earia-border bg-white hover:border-earia-accent/50'}`} data-id="element-64">
                  <div className="flex justify-between items-start w-full mb-2" data-id="element-65">
                    <DollarSign className={`w-5 h-5 ${formData.budget === range.id ? 'text-earia-accent-hover' : 'text-earia-text-body'}`} data-id="element-66" />
                    {formData.budget === range.id && <Check className="w-4 h-4 text-earia-accent" data-id="element-67" />}
                  </div>
                  <div data-id="element-68">
                    <span className="block font-bold text-lg text-earia-text-heading mb-1" data-id="element-69">
                      {range.label}
                    </span>
                    <span className="text-xs text-earia-text-body" data-id="element-70">
                      {range.desc}
                    </span>
                  </div>
                </button>)}
            </div>
          </div>;
      case 4:
        // Preferences
        return <div className="space-y-8" data-id="element-71">
            <h3 className="text-xl font-bold text-earia-text-heading" data-id="element-72">
              Algunas preferencias más
            </h3>

            {/* Year */}
            <div className="space-y-3" data-id="element-73">
              <label className="block text-sm font-medium text-earia-text-heading flex items-center gap-2" data-id="element-74">
                <Calendar className="w-4 h-4 text-earia-accent-hover" data-id="element-75" /> Año
                mínimo preferido
              </label>
              <p className="text-xs text-earia-text-body -mt-1" data-id="element-76">
                🇸🇻 Según la ley de El Salvador, solo se permiten vehículos de{' '}
                {MIN_LEGAL_YEAR} en adelante.
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-77">
                {YEAR_OPTIONS.map(opt => <button type="button" key={opt} onClick={() => updateField('minYear', opt)} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${formData.minYear === opt ? 'bg-earia-accent/10 border-earia-accent text-earia-text-heading' : 'bg-white border-earia-border text-earia-text-body hover:border-earia-accent/30'}`} data-id="element-78">
                    {opt}
                  </button>)}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-3" data-id="element-79">
              <label className="block text-sm font-medium text-earia-text-heading flex items-center gap-2" data-id="element-80">
                <FileText className="w-4 h-4 text-earia-accent-hover" data-id="element-81" />{' '}
                Condición del título
              </label>
              <div className="flex flex-wrap gap-3" data-id="element-82">
                {TITLE_OPTIONS.map(opt => <button type="button" key={opt} onClick={() => updateField('titlePreference', opt)} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${formData.titlePreference === opt ? 'bg-earia-accent/10 border-earia-accent text-earia-text-heading' : 'bg-white border-earia-border text-earia-text-body hover:border-earia-accent/30'}`} data-id="element-83">
                    {opt}
                  </button>)}
              </div>
            </div>

            {/* Experience */}
            <div className="space-y-3" data-id="element-84">
              <label className="block text-sm font-medium text-earia-text-heading flex items-center gap-2" data-id="element-85">
                <HelpCircle className="w-4 h-4 text-earia-accent-hover" data-id="element-86" /> ¿Has
                importado antes?
              </label>
              <div className="grid grid-cols-2 gap-4" data-id="element-87">
                {['Sí, ya tengo experiencia', 'No, es mi primera vez'].map(opt => <button type="button" key={opt} onClick={() => updateField('hasImported', opt)} className={`p-3 rounded-lg text-sm font-medium border transition-all text-center ${formData.hasImported === opt ? 'bg-earia-accent/10 border-earia-accent text-earia-text-heading' : 'bg-white border-earia-border text-earia-text-body hover:border-earia-accent/30'}`} data-id="element-88">
                      {opt}
                    </button>)}
              </div>
            </div>
          </div>;
      case 5:
        // Contact
        return <div className="space-y-5" data-id="element-89">
            <h3 className="text-xl font-bold text-earia-text-heading" data-id="element-90">
              ¿Cómo te contactamos?
            </h3>
            <p className="text-sm text-earia-text-body -mt-4 mb-2" data-id="element-91">
              Te enviaré el análisis preliminar a estos datos.
            </p>

            <div className="space-y-4" data-id="element-92">
              <div data-id="element-93">
                <label htmlFor="name" className="block text-sm font-medium text-earia-text-heading mb-1" data-id="element-94">
                  Nombre Completo
                </label>
                <input type="text" id="name" value={formData.name} onChange={e => updateField('name', e.target.value)} className="w-full bg-white border border-earia-border rounded-lg px-4 py-2 text-earia-text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-earia-accent/50 focus:border-earia-accent transition-all" placeholder="Ej. Juan Pérez" data-id="element-95" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-96">
                <div data-id="element-97">
                  <label htmlFor="email" className="block text-sm font-medium text-earia-text-heading mb-1" data-id="element-98">
                    Correo Electrónico
                  </label>
                  <input type="email" id="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full bg-white border border-earia-border rounded-lg px-4 py-2 text-earia-text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-earia-accent/50 focus:border-earia-accent transition-all" placeholder="juan@ejemplo.com" data-id="element-99" />
                </div>
                <div data-id="element-100">
                  <label htmlFor="phone" className="block text-sm font-medium text-earia-text-heading mb-1" data-id="element-101">
                    Teléfono / WhatsApp
                  </label>
                  <input type="tel" id="phone" value={formData.phone} onChange={e => updateField('phone', e.target.value)} className="w-full bg-white border border-earia-border rounded-lg px-4 py-2 text-earia-text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-earia-accent/50 focus:border-earia-accent transition-all" placeholder="+503 7000-0000" data-id="element-102" />
                </div>
              </div>

              <div data-id="element-103">
                <label htmlFor="message" className="block text-sm font-medium text-earia-text-heading mb-1" data-id="element-104">
                  Mensaje Adicional (Opcional)
                </label>
                <textarea id="message" rows={3} value={formData.message} onChange={e => updateField('message', e.target.value)} className="w-full bg-white border border-earia-border rounded-lg px-4 py-2 text-earia-text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-earia-accent/50 focus:border-earia-accent transition-all" placeholder="¿Alguna duda específica o modelo en mente?" data-id="element-105"></textarea>
              </div>

              {/* Términos y Condiciones checkbox */}
              <div className="flex items-start gap-3 mt-2" data-id="element-106">
                <input type="checkbox" id="acceptTerms" checked={formData.acceptedTerms} onChange={e => updateField('acceptedTerms', e.target.checked)} className="mt-1 h-4 w-4 rounded border-earia-border text-earia-accent focus:ring-earia-accent/50 cursor-pointer accent-[#E6AC00]" data-id="element-107" />
                <label htmlFor="acceptTerms" className="text-sm text-earia-text-body cursor-pointer select-none" data-id="element-108">
                  He leído y acepto los{' '}
                  <a href="https://1drv.ms/b/c/ce9749865f801225/IQAyug5I0317RpWOi9VXPuWQAQ_nPkLh6HQpjRuV5GZ9UQw?e=LaToP5" target="_blank" rel="noopener noreferrer" className="text-earia-accent-hover hover:underline font-medium" data-id="element-109">
                    Términos y Condiciones
                  </a>
                </label>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  // --- Validation for Next Button ---
  const canProceed = () => {
    switch (step) {
      case 1:
        return !!formData.usage;
      case 2:
        return formData.vehicleTypes.length > 0;
      case 3:
        return !!formData.budget;
      case 4:
        return !!formData.minYear && !!formData.titlePreference && !!formData.hasImported;
      case 5:
        return !!formData.name && !!formData.email && !!formData.phone && formData.acceptedTerms;
      default:
        return false;
    }
  };
  return <section id="cta" className="relative py-24 overflow-hidden bg-earia-bg min-h-[800px] flex items-center" data-id="element-110">
      {/* Background - Blurred Dashboard Effect */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-111">
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-4 p-8 opacity-30 blur-sm transform scale-105" data-id="element-112">
          {[...Array(12)].map((_, i) => <div key={i} className="bg-earia-bg-alt rounded-xl border border-earia-border h-40 w-full" data-id="element-113"></div>)}
        </div>
        <div className="absolute inset-0 bg-earia-bg/80 backdrop-blur-sm" data-id="element-114"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-115">
        {isSuccess ? <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-earia-card border border-earia-border rounded-2xl shadow-xl p-12 text-center" data-id="element-116">
            <div className="w-20 h-20 bg-earia-success/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-117">
              <Check className="w-10 h-10 text-earia-success" data-id="element-118" />
            </div>
            <h2 className="text-3xl font-bold text-earia-text-heading mb-4" data-id="element-119">
              ¡Solicitud Recibida!
            </h2>
            <p className="text-earia-text-body text-lg mb-8" data-id="element-120">
              Gracias por compartir tus detalles. Analizaré tu caso y te
              contactaré en menos de 24 horas hábiles con una propuesta
              personalizada.
            </p>
            <button onClick={() => {
          setIsSuccess(false);
          setStep(1);
          setFormData(INITIAL_DATA);
        }} className="text-earia-accent-hover font-bold hover:underline" data-id="element-121">
              Iniciar nueva consulta
            </button>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-earia-card border border-earia-border rounded-2xl shadow-xl overflow-hidden flex flex-col" data-id="element-122">
            {/* Header */}
            <div className="bg-earia-bg-alt/50 p-8 border-b border-earia-border" data-id="element-123">
              <div className="text-center mb-6" data-id="element-124">
                <h2 className="text-2xl md:text-3xl font-bold text-earia-text-heading mb-2" data-id="element-125">
                  Cuéntanos sobre tu próxima inversión
                </h2>
                <p className="text-earia-text-body text-sm md:text-base" data-id="element-126">
                  En pocos pasos cuéntanos lo que necesitás para darte una
                  cotización personalizada.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-earia-border/30 h-2 rounded-full overflow-hidden" data-id="element-127">
                <motion.div className="h-full bg-earia-accent" initial={{
              width: 0
            }} animate={{
              width: `${step / totalSteps * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-128"></motion.div>
              </div>
              <div className="flex justify-between mt-2 text-xs font-medium text-earia-text-body uppercase tracking-wider" data-id="element-129">
                <span data-id="element-130">Paso {step} de 5</span>
                <span data-id="element-131">
                  {step === 1 ? 'Uso' : step === 2 ? 'Vehículo' : step === 3 ? 'Presupuesto' : step === 4 ? 'Preferencias' : 'Contacto'}
                </span>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 md:p-8 min-h-[400px] flex flex-col" data-id="element-132">
              <form onSubmit={handleSubmit} className="flex-1 flex flex-col justify-between" data-id="element-133">
                <div className="relative" data-id="element-134">
                  <AnimatePresence custom={direction} mode="wait" data-id="element-135">
                    <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                  x: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 30
                  },
                  opacity: {
                    duration: 0.2
                  }
                }} className="w-full" data-id="element-136">
                      {renderStep()}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Footer / Navigation */}
                <div className="mt-10 pt-6 border-t border-earia-border flex justify-between items-center" data-id="element-137">
                  {step > 1 ? <button type="button" onClick={prevStep} className="flex items-center gap-2 text-earia-text-body hover:text-earia-text-heading font-medium transition-colors px-4 py-2 rounded-lg hover:bg-earia-bg-alt" data-id="element-138">
                        <ChevronLeft className="w-4 h-4" data-id="element-139" />
                        Atrás
                      </button> : <div data-id="element-140"></div> // Spacer
              }

                  {step < totalSteps ? <button type="button" onClick={nextStep} disabled={!canProceed()} className={`flex items-center gap-2 font-bold py-3 px-6 rounded-lg transition-all shadow-sm ${canProceed() ? 'bg-earia-text-heading text-white hover:bg-earia-text-heading/90 transform hover:-translate-y-0.5' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-141">
                      Siguiente
                      <ChevronRight className="w-4 h-4" data-id="element-142" />
                    </button> : <button type="submit" disabled={!canProceed() || isSubmitting} className={`flex items-center gap-2 font-bold py-3 px-8 rounded-lg transition-all shadow-lg ${canProceed() && !isSubmitting ? 'bg-earia-accent hover:bg-earia-accent-hover text-earia-text-heading transform hover:-translate-y-0.5' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-143">
                      {isSubmitting ? 'Enviando...' : <>
                          <Send className="w-4 h-4" data-id="element-144" />
                          Enviar Solicitud
                        </>}
                    </button>}
                </div>
              </form>
            </div>
          </motion.div>}
      </div>
    </section>;
}